import { Component, OnInit, OnDestroy, ElementRef, Renderer2, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit, inject, input, viewChild, output } from "@angular/core";
import { IPoint, IArea, IPosition } from "../../models";
import { PointerService, DeviceService } from "../../services";
import { zoomInOut } from "../../core-ui.animations";
import { PointAreaComponent } from "../point-area/point-area.component";
import { COLOR, ColorEnum } from "../../core-ui.enums";
import { TinyColor } from "@ctrl/tinycolor";

@Component({
  selector: "app-point",
  templateUrl: "./point.component.html",
  styleUrls: ["./point.component.scss"],
  animations: [zoomInOut],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PointComponent implements OnInit, OnDestroy, AfterViewInit {
  private pointerService = inject(PointerService);
  private renderer = inject(Renderer2);
  private cdr = inject(ChangeDetectorRef);
  private parent = inject(PointAreaComponent, { optional: true });

  readonly data = input<IPoint>(undefined);
  readonly area = input<IArea>(undefined);
  readonly streamId = input<string>(undefined);

  readonly destroyed = output<IPoint>();

  readonly svgRef = viewChild<ElementRef<HTMLOrSVGElement>>("svg");
  readonly circleRef = viewChild<ElementRef<HTMLOrSVGElement>>("circle");
  readonly ringRef = viewChild<ElementRef<HTMLDivElement>>("ring");
  readonly pointRef = viewChild<ElementRef<HTMLDivElement>>("point");

  autoDestroyIn = 800;
  position: IPosition;
  timer;
  countdownTimer;

  normalizedRadius: number;
  circumference: number;
  radius = 25;
  stroke = 4;

  strokeDashoffset: number;

  progress = 100;

  constructor() {
    this.normalizedRadius = this.radius - this.stroke * 2;
    this.circumference = this.normalizedRadius * 2 * Math.PI;
    window.addEventListener("resize", this.resize.bind(this));
  }
  // using hostListener is slower
  // @HostListener('window:resize', ['$event'])
  resize() {
    this.position = this.pointerService.draw(this.parent.area, this.data());
    this.cdr.detectChanges();
  }

  ngOnInit() {
    const data = this.data();
    if (!!data) {
      this.position = this.pointerService.draw(this.area(), data);
      const color = new TinyColor(data.color || COLOR.RED);

      // this.pointerService.draw2(this.area, this.data, this.streamId)
      // .subscribe(position => {
      //   this.position = position;
      //   this.cdr.detectChanges();
      // });

      if (DeviceService.isSafari) {
        this.renderer.setStyle(this.ringRef().nativeElement, "left", "-10px");
      }

      const svgRef = this.svgRef();
      this.renderer.setAttribute(
        svgRef.nativeElement,
        "height",
        String(this.radius * 2)
      );
      this.renderer.setAttribute(
        svgRef.nativeElement,
        "width",
        String(this.radius * 2)
      );
      this.renderer.setAttribute(svgRef.nativeElement, "id", data.id);

      const circleRef = this.circleRef();
      this.renderer.setStyle(
        circleRef.nativeElement,
        "strokeWidth",
        this.stroke
      );
      this.renderer.setStyle(
        circleRef.nativeElement,
        "strokeDasharray",
        this.circumference + " " + this.circumference
      );

      this.renderer.setAttribute(
        circleRef.nativeElement,
        "id",
        data.id
      );
      this.renderer.setAttribute(
        circleRef.nativeElement,
        "r",
        String(this.normalizedRadius)
      );
      this.renderer.setAttribute(
        circleRef.nativeElement,
        "cx",
        String(this.radius)
      );
      this.renderer.setAttribute(
        circleRef.nativeElement,
        "cy",
        String(this.radius)
      );
      // background: $color;
      const pointRef = this.pointRef();
      this.renderer.setStyle(
        pointRef.nativeElement,
        "background-color",
        color.toRgbString()
      );
      // box-shadow: 0 0 0 transparentize($color, 0.6);
      this.renderer.setStyle(
        pointRef.nativeElement,
        "box-shadow",
        ` 0 0 0 ${color.setAlpha(0.6).toRgbString()}`
      );
      // circle { stroke: desaturate(darken($color, 20%), 40%); }
      this.renderer.setStyle(
        circleRef.nativeElement,
        "stroke",
        color.setAlpha(1).darken(20).desaturate(40).toRgbString()
      );

      this.setProgress(this.progress);
      this.setCountdown();
    }
  }

  ngAfterViewInit() {
    // for some reason, chrome didn't render the circle, so we set display to awake the view
    this.renderer.setStyle(this.circleRef().nativeElement, "display", "flex");
  }

  setProgress(percent) {
    const offset = this.circumference - (percent / 100) * this.circumference;
    this.strokeDashoffset = offset;
    this.renderer.setStyle(
      this.circleRef().nativeElement,
      "strokeDashoffset",
      this.strokeDashoffset
    );
  }

  setCountdown() {
    this.countdownTimer = setInterval(() => {
      if (this.progress >= 0) {
        this.progress -= 100 / this.data().timeToLive;
        this.setProgress(this.progress);
      } else {
        clearInterval(this.countdownTimer);
      }
      this.cdr.detectChanges();
    }, 1000);
    this.timer = setTimeout(
      () => this.destroyed.emit(this.data()),
      this.data().timeToLive * 1000 + 300
    );
  }

  clicked(event) {
    event.stopPropagation();
    // for future use. must also send event to refresh the remote points as well
    // clearInterval(this.countdownTimer);
    // clearTimeout(this.timer);
    // this.progress = 100;
    // this.setCountdown();
  }

  ngOnDestroy() {
    clearTimeout(this.timer);
    clearInterval(this.countdownTimer);
    // this.pointerService.untrack(this.data);
  }
}
